import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { SEO } from "@/components/SEO";

const RingSizeGuide = () => {
  return (
    <PageTransition>
      <SEO
        title="מדריך מידת טבעות"
        description="מדריך מידת טבעות של אליס תכשיטים - איך למדוד את מידת הטבעת שלכם בבית. טבלת מידות והוראות מפורטות"
        canonical="https://alice.jewelry/ring-size-guide"
        breadcrumbs={[
          { name: 'דף הבית', url: '/' },
          { name: 'מדריך מידת טבעות', url: '/ring-size-guide' },
        ]}
      />
      <div className="min-h-screen flex flex-col" dir="rtl">
        <Header />

        <main className="flex-1">
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-4xl">
              <div className="text-center mb-12 animate-fade-in">
                <div className="gold-divider mb-6" />
                <h1 className="text-4xl md:text-5xl font-serif mb-4">מדריך מידת טבעות</h1>
                <p className="text-lg text-muted-foreground">
                  הכנו עבורכן/ם מדריך מידות שיעזור לכן/ם להזמין את הטבעת במידה המתאימה
                </p>
              </div>

              <div className="space-y-8 text-right animate-fade-in" style={{ animationDelay: "0.1s" }}>
                <div className="bg-muted/30 p-6 rounded-lg">
                  <p className="text-muted-foreground leading-relaxed">
                    <strong>חשוב לציין:</strong> מידת הטבעות משתנה מיד ימין ליד שמאל וכמובן תלויה במזג האוויר ובמאמץ של
                    האצבע טרם המדידה, לכן מומלץ לבצע את המדידה לאחר מנוחה ובתנאי מזג אויר סבירים.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-serif mb-4">אביזרים נדרשים לבדיקה:</h2>
                  <ol className="space-y-2 text-muted-foreground">
                    <li>1. חוט</li>
                    <li>2. סרגל</li>
                    <li>3. אצבע</li>
                  </ol>
                </div>

                <div className="bg-primary/5 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-3">יש לכם כבר טבעת?</h3>
                  <p className="text-muted-foreground mb-4">
                    במידה ויש לכם כבר טבעת ואתם רוצים לברר מה המידה שלה, ניתן להיעזר באפליקציית איתור המידה:
                  </p>
                  <div className="space-y-2">
                    <p className="text-sm">
                      <a
                        href="https://play.google.com/store/apps/details?id=peregrina.tools.droid.ring_size_finder"
                        className="text-primary hover:underline"
                      >
                        להורדת האפליקציה לאנדרואיד, לחצו כאן!
                      </a>
                    </p>
                    <p className="text-sm">
                      <a
                        href="https://apps.apple.com/us/app/the-ring-size/id1513556675"
                        className="text-primary hover:underline"
                      >
                        להורדת האפליקציה לאייפון, לחצו כאן!
                      </a>
                    </p>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-serif mb-4">אוקיי, מתחילים:</h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary font-semibold">1</span>
                      </div>
                      <p className="text-muted-foreground pt-1">
                        עם חוט תפירה, או כל חוט אחר, מקיפים את האצבע הרצויה על החלק העבה ביותר שלה.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary font-semibold">2</span>
                      </div>
                      <p className="text-muted-foreground pt-1">
                        סמנו בעזרת כלי כתיבה את המקום הרצוי על החוט המסמל את היקף האצבע.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary font-semibold">3</span>
                      </div>
                      <p className="text-muted-foreground pt-1">
                        הניחו את החוט על סרגל, בידקו את אורך החוט עד האזור המסומן והשוו לפי הטבלה הבאה:
                      </p>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <h3 className="text-xl font-semibold mb-4">טבלת מידות</h3>
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-primary/10">
                        <th className="border border-border p-3 text-right">מידה לפי היקף אצבע במ"מ</th>
                        <th className="border border-border p-3 text-right">מידה לפי מספר</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        [44, 4],
                        [45, 5],
                        [46, 6],
                        [47, 7],
                        [48, 8],
                        [49, 9],
                        [50, 10],
                        [51, 11],
                        [52, 12],
                        [53, 13],
                        [54, 14],
                        [55, 15],
                        [56, 16],
                        [57, 17],
                        [58, 18],
                        [59, 19],
                        [60, 20],
                        [61, 21],
                        [62, 22],
                        [63, 23],
                        [64, 24],
                        [65, 25],
                        [66, 26],
                        [67, 27],
                        [68, 28],
                        [69, 29],
                        [70, 30],
                        [71, 31],
                      ].map(([mm, size]) => (
                        <tr key={size} className="hover:bg-muted/20">
                          <td className="border border-border p-3">{mm}</td>
                          <td className="border border-border p-3">{size}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer2 />
      </div>
    </PageTransition>
  );
};

export default RingSizeGuide;
